import { useState, useEffect, useContext } from 'react';
import OlMap from 'ol/Map';
import OlView from 'ol/View';
import { defaults as defaultControls, ScaleLine, Attribution } from 'ol/control';
import { defaults as defaultInteractions } from 'ol/interaction';
import OlLayerTile from 'ol/layer/Tile';
import OlSourceOSM from 'ol/source/OSM';
import OlSourceXYZ from 'ol/source/XYZ';
import { GlobalContext } from '../../store/Store';
import  ActionType from '../../store/ActionType';
import './PublicMap.css';

import 'ol/ol.css';

// layers
const OSMLayerSatellite = new OlLayerTile({  
  visible: false,  
  source: new OlSourceXYZ({
    url: 'https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=uk2JFjTRoPbgvaXoG2Kt',
    attributions: '© <a href="https://www.maptiler.com/" target="_blank">MapTiler contributors</a>',
  }), 
});
OSMLayerSatellite.set('title', 'Maptiler satellite');
OSMLayerSatellite.set('type', 'base');
OSMLayerSatellite.set('baseLayer', true);
OSMLayerSatellite.set('noSwitcherDelete', true);

const OSMLayerHybrid = new OlLayerTile({ 
  visible: false, 
  source: new OlSourceXYZ({
    url: 'https://api.maptiler.com/maps/hybrid/{z}/{x}/{y}.jpg?key=uk2JFjTRoPbgvaXoG2Kt',
    attributions: '© <a href="https://www.maptiler.com/" target="_blank">MapTiler contributors</a>',
  }),
});
OSMLayerHybrid.set('title', 'Maptiler hybrid');
OSMLayerHybrid.set('type', 'base');
OSMLayerHybrid.set('baseLayer', true);
OSMLayerHybrid.set('noSwitcherDelete', true);

const basicMap = new OlLayerTile({  
  visible: false, 
  source: new OlSourceOSM({
    attributions:
      '© <a href="https://openstreetmap.org/" target="_blank">OpenStreetMap contributors</a>',
  }),
});
basicMap.set('title', 'Open street map');
basicMap.set('type', 'base');
basicMap.set('baseLayer', true);

const blankMap = new OlLayerTile({
  visible: false, 
  source: undefined,
});
blankMap.set('title', 'No base map');
blankMap.set('type', 'base');
blankMap.set('baseLayer', true);
blankMap.set('noSwitcherDelete', true);
interface Props {
  selectedLayer: number;
}

const PublicMap = (props: Props) => {
  const [initialized, setInitialized] = useState(false);
  const [mapTargetInitialized, setTargetInitalized] = useState(false);
  const [center /* , setCenter */] = useState([0, 0]);
  const [zoom /* , setZoom */] = useState(3);
  const [backgroundLayers /* , setBackgroundLayers */] = useState([
    OSMLayerSatellite,
    OSMLayerHybrid,
    basicMap,
    blankMap,
  ]);

  // global store
  const [globalState, globalDispatch] = useContext(GlobalContext);

  if (!initialized) {
    setInitialized(true);
    const olmap = new OlMap({
      target: null,
      layers: backgroundLayers,
      view: new OlView({
        center: center,
        zoom: zoom,
      }),
      controls: defaultControls({ zoom: false, attribution: false, rotate: false }),
      interactions: defaultInteractions({ altShiftDragRotate: false, pinchRotate: false }),
    });
    // Save map in global store
    // Hack to use `setTimeout` to prevent ugly warning.
    // See this bug: https://dhigroup.visualstudio.com/MIKE/_workitems/edit/22858
    setTimeout(() => globalDispatch({ type: ActionType.SET_MAP, data: olmap }));
  }

  if (backgroundLayers) {
    // update visible background layer
    for (let i = 0; i < backgroundLayers.length; i++) {
      backgroundLayers[i].setVisible(false);
    }
    // console.log('selectedLayerProp: ', props.selectedLayer );
    backgroundLayers[props.selectedLayer].setVisible(true);
  }

  useEffect(() => {
    if (!mapTargetInitialized && (globalState.map as any).olMap) {
      setTargetInitalized(true);
      (globalState.map as any).olMap.setTarget('map');
      const scaleline = new ScaleLine({ target: document.querySelector('#map') as HTMLElement });
      (globalState.map as any).olMap.addControl(scaleline);
      const attribution = new Attribution({
        collapsed: false,
        collapsible: false,
        target: document.querySelector('#map') as HTMLElement,
      });
      (globalState.map as any).olMap.addControl(attribution);
      //Resolved White-Page flicking on project switch
      return () => (globalState.map as any).olMap.setTarget(undefined);
    }
  }, [(globalState.map as any).olMap]);

  useEffect(() => {
    // console.log('mounting map...', );

    // Same as componentWillUnmount(), called right before the Map unmounts
    return () => {
      // console.log('unmounting map, updating store about this...');
      // remove map from globalStore
      globalDispatch({ type: ActionType.SET_MESH_FEATURES, data: null });
      globalDispatch({ type: ActionType.SET_MAP, data: null });
      globalDispatch({ type: ActionType.SET_MESH_PROJECTION, data: null });
    };
  }, []);

  // TODO: consider implementing something similar in case of performance problems
  // shouldComponentUpdate(nextProps, nextState) {
  //   let center = olmap.getView().getCenter();
  //   let zoom = olmap.getView().getZoom();
  //   if (center === nextState.center && zoom === nextState.zoom && nextProps.selectedLayer === selectedLayer) return false;

  //   this.setState({selectedLayer: this.props.selectedLayer});
  //   return true;
  // }

  return (
    <div id="fullscreenViewPoint">
      <div id="map"> </div>
    </div>
  );
};

export default PublicMap;
